import React, { useState, useEffect } from 'react';
import Pluralize from 'pluralize';
import { produce } from 'immer';
import { uniq, difference } from 'lodash';

import {
  IAccount,
  useGetAccount,
  IAccountBaseSettings,
  IAccountSettings,
  useGetAccountSettings
} from '../../libs/account';
import { IUser, useGetUsersByID } from '../../libs/user';
import { IContact, useGetContactsByID } from '../../libs/contact';
import { IsInIframe } from '../../libs/browser';
import { TermBase, Terms, Term } from '../terms/types';

export type { Terms };

const DEFAULT_TERMS: TermBase = {
  patient: 'patient',
  provider: 'provider',
  mrn: 'MRN'
};

const Capitalize = (word: string): string => {
  const parts = word.split(' ');
  return parts
    .map((part) => {
      return part[0].toUpperCase() + part.slice(1);
    })
    .join(' ');
};

const GetTermsFromBase = (base: TermBase): Terms => {
  const terms: Record<string, Term> = {};
  for (const key of Object.keys(base)) {
    const value: string = base[key];
    const plural = Pluralize(value);
    terms[key] = {
      default: value,
      plural: plural,
      capitalized: Capitalize(value),
      pluralCapitalized: Capitalize(plural)
    };
  }
  return terms as Terms;
};

export interface MetaDataValue {
  account: IAccount;
  updateAccount: (account: IAccount) => void;
  contacts: Record<number, IContact>;
  fetchContacts: (ids: number[]) => void;
  users: Record<number, IUser>;
  fetchUsers: (ids: number[]) => void;
  settings: IAccountBaseSettings;
  isEmbedded: boolean;
  isReadOnly: boolean;
  isLightbeam: boolean;
  isLoading: boolean;
  updateSettings: (settings: IAccountSettings) => void;
  terms: Terms;
  updateTerms: (base: TermBase) => void;
}

const MetaDataContext = React.createContext<MetaDataValue>({
  account: null,
  updateAccount: null,
  contacts: null,
  fetchContacts: null,
  users: null,
  fetchUsers: null,
  settings: null,
  isEmbedded: null,
  isReadOnly: null,
  isLightbeam: null,
  isLoading: true,
  updateSettings: null,
  terms: null,
  updateTerms: null
});

export const MetaDataProvider: React.FC = (props) => {
  // Account
  const { account, setAccount } = useGetAccount();
  // Account Settings
  const { settings, setSettings, isLoading } = useGetAccountSettings();
  // Terms
  const [termsBase, setTermsBase] = useState<TermBase>(null);
  useEffect(() => {
    setTermsBase(settings?.terms || null);
  }, [settings?.terms]);
  const terms = GetTermsFromBase(termsBase || DEFAULT_TERMS);
  const updateTerms = (base: TermBase) => {
    setTermsBase(base);
  };
  // Contacts
  const [contacts, setContacts] = useState<Record<number, IContact>>({});
  const [contactIDs, setContactIDs] = useState<number[]>([]);
  const { contacts: _contacts } = useGetContactsByID({
    ids: contactIDs
  });
  useEffect(() => {
    if (!_contacts) {
      return;
    }
    const newContacts = produce(contacts, (draft) => {
      for (const contact of _contacts) {
        draft[contact.id] = contact;
      }
    });
    setContacts(newContacts);
  }, [_contacts]);
  // Users
  const [users, setUsers] = useState<Record<number, IUser>>({});
  const [userIDs, setUserIDs] = useState<number[]>([]);
  const { users: _users } = useGetUsersByID({
    ids: userIDs
  });
  useEffect(() => {
    if (!_users) {
      return;
    }
    const newUsers = produce(users, (draft) => {
      for (const user of _users) {
        draft[user.id] = user;
      }
    });
    setUsers(newUsers);
  }, [_users]);
  // Lightbeam
  let isLightbeam = false;
  if (account && account.isLightbeam) {
    isLightbeam = true;
  }
  // Read-only mode
  const isEmbedded = IsInIframe();
  let isReadOnly = false;
  if (isEmbedded && !isLightbeam) {
    isReadOnly = true;
  }
  // Compose
  const value: MetaDataValue = {
    account,
    updateAccount: setAccount,
    contacts,
    fetchContacts: (ids) => {
      if (ids?.length > 0) {
        ids = difference(
          uniq(ids),
          Object.keys(contacts).map((id) => parseInt(id))
        );
        if (ids.length > 0) {
          setContactIDs(ids);
        }
      }
    },
    users,
    fetchUsers: (ids) => {
      if (ids?.length > 0) {
        ids = difference(
          uniq(ids),
          Object.keys(users).map((id) => parseInt(id))
        );
        if (ids.length > 0) {
          setUserIDs(ids);
        }
      }
    },
    settings,
    isEmbedded,
    isReadOnly,
    isLightbeam,
    isLoading,
    updateSettings: setSettings,
    terms,
    updateTerms
  };
  return <MetaDataContext.Provider value={value} {...props} />;
};

export function useMetaData(): MetaDataValue {
  const context = React.useContext(MetaDataContext);
  if (context === undefined) {
    throw new Error('useMetaData must be used within a MetaDataProvider');
  }
  return context;
}
